'use client';

import React from 'react';
import { IconGoodCause, IconCheckCircle, IconCrossCircle, IconChevronRight } from '@repo/svgs';
import { Divider } from '@/components/ui/Divider';
import { ListCell } from '@/components/ui/ListCell';
import { TextCell } from '@/components/ui/TextCell';
import { ResponsiveLink } from '@/components/shared/ResponsiveLink';

interface GoodCauseItemProps {
  icon: React.ElementType;
  text: string;
  isPositive?: boolean;
}

const GoodCauseItem = ({ icon: Icon, text, isPositive = true }: GoodCauseItemProps) => (
  <ListCell
    leadingIcon={<Icon className={`w-6 h-6 ${isPositive ? 'text-success' : 'text-secondary'}`} />}
    title={text}
    trailing="none"
  />
);

export function GoodCauseModal() {
  return (
    <div className="flex flex-col gap-4">
      {/* Header Section */}
      <div className="flex flex-col gap-4 px-2">
        <IconGoodCause className="w-8 h-8 text-icon-good-cause" />
        <p className="text-body-2 text-primary">
          Data suggests that Good Cause Eviction applies to this building or buildings owned by this
          landlord.
        </p>
      </div>

      {/* Positive Benefits List */}
      <div className="flex flex-col gap-1">
        <GoodCauseItem
          icon={IconCheckCircle}
          text="This limits rent increases on market-rate apartments."
          isPositive={true}
        />

        <div className="px-2">
          <Divider />
        </div>

        <GoodCauseItem
          icon={IconCheckCircle}
          text="Entitles tenants to a lease renewal if they uphold the terms of their lease."
          isPositive={true}
        />

        <div className="px-2">
          <Divider />
        </div>

        <GoodCauseItem
          icon={IconCheckCircle}
          text="Tenants can challenge increases above 10% or 5% + inflation (whichever is lower)."
          isPositive={true}
        />
      </div>

      {/* Does Not Apply Section */}
      <div className="flex flex-col gap-1">
        <TextCell className="bg-primary rounded-lg">
          <span className="text-body-2-high text-primary">
            Good Cause Eviction does not apply to:
          </span>
        </TextCell>

        <GoodCauseItem
          icon={IconCrossCircle}
          text="Buildings built after 2009, condos, coops, hotels, dorms, regulated/stabilized units, or to buildings less than 10 units where the landlord resides."
          isPositive={false}
        />

        <div className="px-2">
          <Divider />
        </div>

        <div className="flex gap-4 items-start px-2 py-3">
          <IconCrossCircle className="w-6 h-6 text-secondary mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="text-body-2 text-secondary">
              Landlords who own less than 10 units overall. It does not apply to renters who's rents
              are 245%+ of the{' '}
              <button
                onClick={() =>
                  window.open('https://www.huduser.gov/portal/datasets/fmr.html', '_blank')
                }
                className="text-accent underline hover:no-underline"
              >
                local Fair Market Rent
              </button>
              .
            </p>
          </div>
        </div>
      </div>

      {/* Learn More Button */}
      <div className="px-2">
        <ResponsiveLink
          href={'/blogs/what-is-good-cause-eviction-what-does-it-mean-for-new-yorkers'}
          className="flex items-center gap-1 py-2 group"
        >
          <span className="text-body-2-high text-accent">Learn more about Good Cause Eviction</span>
          <IconChevronRight className="w-3 h-3 text-accent transition-transform group-hover:translate-x-0.5" />
        </ResponsiveLink>
      </div>
    </div>
  );
}
